import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { AddonSuggestionModal } from '@/components/tickets/AddonSuggestionModal';
import { evaluateAddonSuggestions } from '@/utils/addonSuggestionRules';
import { attachAdasEvidence, loadAdasAdvisoryEvidence } from '@/utils/adasAdvisoryEvidence';
import { recordAddonSuggestionEvent } from '@/utils/addonSuggestionOutcome';
import { addCatalogJobsToTicket } from '@/utils/addCatalogJobsToTicket';
import {
  filterSuggestionsAlreadyOnTicket,
  mapAddonSettingsRow,
  toEvaluatedJobs,
  type AddonCatalogLabels,
  type TicketWorkItem,
} from '@/utils/addonSuggestionTicketState';
import { breadcrumbs } from '@/telemetry/breadcrumbs';
import { notifyDayboardInvalidated } from '@/utils/dayboardInvalidation';
import { toast } from '@/hooks/use-toast';
import type {
  AddonSettings,
  AddonSuggestion,
  AddonSuggestionType,
  JobRelationship,
} from '@/types/addonSuggestions';

/**
 * CORE-BP item 6 — the one place a post-add suggestion is asked.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHY A PROVIDER RATHER THAN A UTILITY PER CALL SITE
 * ═══════════════════════════════════════════════════════════════════════════
 * Six production paths can put work on an existing ticket, and two of them are
 * hooks (`useApplyCannedJob`, `useDviSession`/`useDviEstimate`) with no
 * component of their own to render a dialog in. A utility would have forced
 * every one of them to own modal state, an accept handler and an outcome
 * write — six copies of the logic that Slice B already had to consolidate once
 * because two copies had drifted.
 *
 * So the question is asked in exactly one place. A mutation path's entire
 * obligation is one line, AFTER its own success:
 *
 *     await evaluateAfterWorkAdded(ticketId);
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * AFTER SUCCESS, AND AGAINST THE SETTLED STATE
 * ═══════════════════════════════════════════════════════════════════════════
 * Everything is re-read from the database at evaluation time rather than
 * passed in by the caller. That is deliberate: the caller knows what it just
 * added, and what BP must be asked about is what the ticket NOW HAS. A
 * suggestion derived from one new row in isolation would miss the suspension
 * job that was already there — and CORE-BP's rules are about the combination.
 *
 * Re-reading is also what makes failure safe. A caller that failed to add
 * cannot produce a suggestion about work that does not exist, because nothing
 * it believed is trusted; the ticket is asked instead.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHY IT RE-EVALUATES AFTER ITS OWN ACCEPT, AND WHY THAT CANNOT LOOP
 * ═══════════════════════════════════════════════════════════════════════════
 * Accepting a suggestion adds work to the ticket, which is the very event that
 * triggers evaluation — so this is the one place a loop could form.
 *
 * It re-evaluates anyway, because skipping it would lose real obligations. A
 * strut job suggests alignment; alignment on the ticket is itself what the SAS
 * rule reads ("rack/column/alignment can invalidate SAS"), and SAS was NOT in
 * the first showing. Measured, not assumed: the first draft of this provider
 * skipped the re-evaluation and a test of the real rules showed the SAS
 * obligation silently disappearing.
 *
 * It terminates by construction. `addCatalogJobsToTicket` stamps the accepted
 * job with the SHOP'S CONFIGURED catalogue id — the exact identity the
 * already-present guard reads — so an accepted family can never be offered
 * again. There are four families, so a chain is at most four showings deep.
 *
 * The one way that argument could fail is an accept that does NOT land: an
 * unmapped family is never added, so it would still be absent and would be
 * asked again, forever. Hence re-evaluation happens ONLY when everything the
 * human accepted genuinely became work.
 *
 * Nothing is suppressed for having come from a suggestion. The loop is broken
 * by the work existing, not by anyone being told to stop asking.
 */

interface AddonSuggestionTrigger {
  /**
   * Evaluate CORE-BP against a ticket's CURRENT complete work state.
   * Call only after work has genuinely been added. Never throws.
   */
  evaluateAfterWorkAdded: (ticketId: string) => Promise<void>;
}

const Ctx = createContext<AddonSuggestionTrigger>({
  // A call site outside the provider must not crash a successful work-add.
  evaluateAfterWorkAdded: async () => {},
});

export const useAddonSuggestionTrigger = (): AddonSuggestionTrigger => useContext(Ctx);

interface PendingShowing {
  ticketId: string;
  shopId: string;
  vehicle: { year: number; make: string; model: string };
  jobNames: string[];
  suggestions: AddonSuggestion[];
  settings: AddonSettings;
}

export function AddonSuggestionProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<PendingShowing | null>(null);
  /**
   * One question at a time, and never one that overwrites an unanswered one.
   *
   * `busy` covers two mutations resolving together. `showing` covers the case
   * mutation testing actually found: an add landing WHILE the advisor is still
   * deciding replaced the open dialog with a different question, so the first
   * showing produced no outcome at all — silently breaking Slice B's
   * "exactly one outcome per showing" through a door item 6 had just opened.
   *
   * The skipped evaluation is not lost work: the next add on this ticket
   * evaluates the settled state, which by then includes everything.
   */
  const busy = useRef(false);
  const showing = useRef(false);

  const evaluateAfterWorkAdded = useCallback(async (ticketId: string) => {
    if (!ticketId || busy.current || showing.current) return;
    busy.current = true;
    try {
      // ── the ticket and its vehicle ──────────────────────────────────────
      const { data: ticket, error: ticketErr } = await supabase
        .from('tickets')
        .select('id, shop_id, vehicle_id, vehicle:vehicles(year, make, model)')
        .eq('id', ticketId)
        .is('deleted_at', null)
        .maybeSingle();

      if (ticketErr || !ticket?.shop_id) return;
      const vehicleRow = (ticket as { vehicle?: { year?: number; make?: string; model?: string } })
        .vehicle;
      const vehicle = {
        year: Number(vehicleRow?.year ?? 0),
        make: String(vehicleRow?.make ?? ''),
        model: String(vehicleRow?.model ?? ''),
      };
      const shopId = ticket.shop_id as string;

      // ── the ticket's COMPLETE current work, with catalogue identity ─────
      const { data: ticketJobs, error: jobsErr } = await supabase
        .from('ticket_jobs')
        .select('job:jobs(name, catalog_job_id)')
        .eq('ticket_id', ticketId);

      if (jobsErr) return;
      const work: TicketWorkItem[] = (ticketJobs ?? [])
        .map((r) => (r as { job?: { name?: string; catalog_job_id?: string | null } }).job)
        .filter((j): j is { name?: string; catalog_job_id?: string | null } => !!j)
        .map((j) => ({ name: String(j.name ?? ''), catalogJobId: j.catalog_job_id ?? null }))
        .filter((w) => w.name.length > 0);

      if (work.length === 0) return;

      // ── the shop's current settings ─────────────────────────────────────
      const { data: settingsRow } = await supabase
        .from('addon_suggestion_settings')
        .select('*')
        .eq('shop_id', shopId)
        .maybeSingle();
      const settings = mapAddonSettingsRow(settingsRow as never);
      if (!settings.featureEnabled) return;

      // ── permitted relationship evidence (Slice A: followup only) ────────
      const evaluatedJobs = toEvaluatedJobs(work);
      const catalogIds = evaluatedJobs
        .map((j) => j.catalogJobId)
        .filter((id): id is string => !!id);

      let relationships: JobRelationship[] = [];
      if (catalogIds.length > 0) {
        const { data: adjacency, error: adjacencyError } = await supabase
          .from('job_adjacency_map')
          .select('source_job_id, related_job_id, relationship_type')
          .in('source_job_id', catalogIds)
          .eq('relationship_type', 'followup');

        if (adjacencyError) {
          // Degrade to keyword evidence rather than losing the evaluation, and
          // say so — the same contract the creation path already holds.
          breadcrumbs.log('OB:GUARD', {
            action: 'addon_relationships_unavailable',
            reason: adjacencyError.message,
          });
        } else {
          relationships = (adjacency ?? []).map((a) => ({
            sourceJobId: a.source_job_id,
            relatedJobId: a.related_job_id,
            relationshipType: a.relationship_type as JobRelationship['relationshipType'],
          }));
        }
      }

      const suggestions = evaluateAddonSuggestions(
        evaluatedJobs, vehicle, settings, relationships);
      if (suggestions.length === 0) return;

      // ── do not ask for work the ticket already has ──────────────────────
      const configuredIds = Object.values(settings.catalogJobIds)
        .filter((id): id is string => !!id);
      const catalogLabels: AddonCatalogLabels = {};
      if (configuredIds.length > 0) {
        const { data: labelRows } = await supabase
          .from('jobs_catalog').select('id, label').in('id', configuredIds);
        const labelById = new Map((labelRows ?? []).map((r) => [r.id as string, r.label as string]));
        (Object.keys(settings.catalogJobIds) as AddonSuggestionType[]).forEach((t) => {
          const id = settings.catalogJobIds[t];
          const label = id ? labelById.get(id) : undefined;
          if (label) catalogLabels[t] = label;
        });
      }

      const remaining = filterSuggestionsAlreadyOnTicket(
        suggestions, work, settings, catalogLabels);
      if (remaining.length === 0) return;

      // OptiVehicle Phase 3A: explanation only. Positive recorded ADAS equipment
      // may be shown under an 'adas' suggestion that ALREADY exists; it never
      // creates, removes or pre-checks one. Read only when it could be shown.
      const adasEvidence = remaining.some((s) => s.type === 'adas')
        ? await loadAdasAdvisoryEvidence((ticket as { vehicle_id?: string | null }).vehicle_id ?? null)
        : null;

      showing.current = true;
      setPending({
        ticketId, shopId, vehicle,
        jobNames: work.map((w) => w.name),
        suggestions: attachAdasEvidence(remaining, adasEvidence),
        settings,
      });
    } catch (e) {
      // A suggestion is a question. Failing to ask it must never surface as an
      // error on top of a work-add that genuinely succeeded.
      breadcrumbs.log('OB:GUARD', {
        action: 'addon_post_add_evaluation_failed',
        reason: e instanceof Error ? e.message : String(e),
        ticketId,
      });
    } finally {
      busy.current = false;
    }
  }, []);

  /** One outcome per showing, through the Slice B recorder. Never awaited. */
  const record = useCallback((showing: PendingShowing, accepted?: AddonSuggestionType[]) => {
    recordAddonSuggestionEvent(supabase, {
      shopId: showing.shopId,
      vehicle: showing.vehicle,
      jobNames: showing.jobNames,
      suggestions: showing.suggestions,
      accepted,
    });
  }, []);

  const handleAccept = useCallback(async (accepted: AddonSuggestionType[]) => {
    const showing = pending;
    if (!showing) return;

    // Recorded from the human's answer, before any add can fail. What the
    // advisor decided is true whether or not the catalogue can honour it.
    record(showing, accepted);

    const catalogJobIds = accepted
      .map((t) => showing.settings.catalogJobIds[t])
      .filter((id): id is string => !!id);

    const unmapped = accepted.filter((t) => !showing.settings.catalogJobIds[t]);
    if (unmapped.length > 0) {
      // Honest rather than silent: the shop has not told us which catalogue job
      // this family means, so the work cannot be added by identity.
      toast({
        title: 'Not configured yet',
        description:
          `No catalogue job is mapped for: ${unmapped.join(', ')}. Set it in Add-On Suggestion Settings.`,
        variant: 'destructive',
      });
    }

    if (catalogJobIds.length === 0) return;

    const outcome = await addCatalogJobsToTicket(showing.ticketId, catalogJobIds);
    if (outcome.failed.length > 0) {
      toast({
        title: 'Some suggested work was not added',
        description: outcome.failed.map((f) => f.reason).join('; '),
        variant: 'destructive',
      });
    }
    if (outcome.addedJobIds.length > 0) {
      void notifyDayboardInvalidated({
        reason: 'job-add', shopId: showing.shopId, ticketId: showing.ticketId,
      });
    }

    // The accepted work is now ordinary work on this ticket, so the ticket is
    // asked again — see the header. Only when EVERYTHING the human accepted
    // landed: a family that failed to be added is still absent, and asking
    // about it again is the one shape of this that would not terminate.
    const everythingLanded =
      unmapped.length === 0 &&
      outcome.failed.length === 0 &&
      outcome.addedJobIds.length === catalogJobIds.length;
    if (everythingLanded) {
      await evaluateAfterWorkAdded(showing.ticketId);
    }
  }, [pending, record, evaluateAfterWorkAdded]);

  return (
    <Ctx.Provider value={{ evaluateAfterWorkAdded }}>
      {children}
      {pending && (
        <AddonSuggestionModal
          isOpen
          suggestions={pending.suggestions}
          vehicleInfo={pending.vehicle}
          mode="existing-ticket"
          onAccept={handleAccept}
          // "Create without" on an existing ticket declines everything shown.
          onDismiss={() => { if (pending) record(pending, []); }}
          // Escape, the backdrop and the X — Slice B's third exit, which must
          // also produce exactly one outcome.
          onCloseWithoutChoice={() => { if (pending) record(pending, []); }}
          onClose={() => { showing.current = false; setPending(null); }}
        />
      )}
    </Ctx.Provider>
  );
}
